import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';
import type { ReactNode } from 'react';
import type React from 'react';

import { api } from '@connected/api-client';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Label } from '@connected/form-fields/components/label';
import { SingleHorizontalOptionItem, SingleOptionField } from '@connected/form-fields/components/option';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';

import type { MergedChecklistItems } from '@/hooks/queries/use-checklist';

import { ObservationComponent } from '@/components/features/observation';
import { useChecklists } from '@/hooks/queries/use-checklist';
import { queryClient } from '@/lib/query-client';

type CheckListItemFormValues = {
    conclusionKind: components['schemas']['ListElectricalProjectCheckListItemQueryResultItem']['ConclusionKind'];
};

export function CheckList({
    title,
    kind,
    projectVersionId,
    disabled = false,
    before,
    after,
}: {
    title: string;
    kind: components['schemas']['ElectricalCheckListItemKind'];
    projectVersionId: string;
    disabled?: boolean;
    before?: ReactNode;
    after?: ReactNode;
}) {
    const { data } = useChecklists({
        electricalProjectVersionId: projectVersionId,
        checklistKind: 'Project',
        objectKind: kind,
        objectId: projectVersionId,
    });

    return (
        <Container className="flex flex-col gap-4">
            <Container.Header>
                <Container.Title>{title}</Container.Title>
            </Container.Header>
            <Container.Content>
                {before}

                {data?.items?.map(item => (
                    <ChecklistItem
                        key={item.ElectricalProjectCheckListItemId}
                        item={item}
                        projectVersionId={projectVersionId}
                        disabled={disabled}
                    />
                ))}

                {data.count === 0 && (
                    <div className="text-center text-gray-500">
                        Geen "
                        {title}
                        " punten zijn  gevonden.
                    </div>
                )}

                {after}
            </Container.Content>
        </Container>
    );
}

function ChecklistItem({
    item,
    projectVersionId,
    disabled = false,
}: {
    item: MergedChecklistItems;
    projectVersionId: string;
    disabled?: boolean;
}) {
    const { context: { electricalProjectVersionId } } = useElectricalProject();

    const checkListItemMutation = api.useMutation(
        'put',
        '/api/v2/electrical/projects/checklists/items/edit',
        {
            onMutate: async (variables) => {
                const checkListQueryOptions = elektraAppQueryOptions.project.checkListItemsOptions({
                    electricalProjectVersionId: projectVersionId,
                });

                await queryClient.cancelQueries(elektraAppQueryOptions.project.recapOptions({ electricalProjectVersionId: projectVersionId }));
                await queryClient.cancelQueries(checkListQueryOptions);

                const previousDataList = queryClient.getQueryData(checkListQueryOptions.queryKey) as components['schemas']['ListElectricalProjectCheckListItemQueryResult'];

                queryClient.setQueryData<components['schemas']['ListElectricalProjectCheckListItemQueryResult']>(
                    checkListQueryOptions.queryKey,
                    (old) => {
                        if (old) {
                            const updatedData: components['schemas']['ListElectricalProjectCheckListItemQueryResult'] = {
                                ...old,
                                Items: old.Items.map(checklistItem =>
                                    checklistItem.ElectricalProjectCheckListItemId === variables!.body!.ElectricalProjectCheckListItemId
                                        ? { ...checklistItem, ConclusionKind: variables!.body!.ConclusionKind }
                                        : checklistItem,
                                ),
                            };
                            return updatedData;
                        }
                        return old;
                    },
                );

                return { previousDataList };
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(elektraAppQueryOptions.project.checkListItemsOptions({
                    electricalProjectVersionId: projectVersionId,
                }));

                await queryClient.invalidateQueries(elektraAppQueryOptions.project.recapOptions({ electricalProjectVersionId: projectVersionId }));
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    );

    const form = useForm<CheckListItemFormValues, ZodValidator>({
        defaultValues: {
            conclusionKind: item.ConclusionKind || null,
        },
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            await checkListItemMutation.mutateAsync({
                body: {
                    ElectricalProjectCheckListItemId:
                        item.ElectricalProjectCheckListItemId!,
                    ConclusionKind: value.conclusionKind,
                },
            });
        },
    });

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await form.handleSubmit();
    }

    return (

        <form onSubmit={onSubmit} className="flex flex-col gap-2">
            <form.Field name="conclusionKind">
                {({ handleChange, state, name }) => (
                    <Label label={item.Description} htmlFor={name}>
                        <SingleOptionField
                            name={name}
                            handleChange={(e) => {
                                handleChange(e as 'Approved' | 'NotApproved' | 'NotApplicable' | null | undefined);
                                form.handleSubmit();
                            }}
                            state={state}
                            className="flex flex-row items-center justify-between"
                            options={[
                                { label: item?.PositiveLabelText || 'Akkoord', value: 'Approved' },
                                { label: item?.NegativeLabelText || 'Niet Akkoord', value: 'NotApproved' },
                                ...(item?.EnableNotApplicable ? [{ label: item?.NotApplicableLabelText || 'N.V.T.', value: 'NotApplicable' }] : []),
                            ]}
                            render={SingleHorizontalOptionItem}
                            disabled={disabled}
                        />
                    </Label>
                )}
            </form.Field>
            <form.Subscribe selector={({ values }) => [values.conclusionKind]}>
                {([conclusionKind]) => (
                    <div>
                        {(conclusionKind === 'NotApproved' && item?.EnableObservations && item.Kind) && (
                            <div>
                                <ObservationComponent
                                    electricalProjectVersionId={projectVersionId}
                                    objectId={item.ElectricalProjectCheckListItemId!}
                                    objectKind="CheckListItem"
                                    checkListItemKind={item.Kind}
                                />
                            </div>
                        )}
                    </div>
                )}
            </form.Subscribe>
        </form>
    );
}
